import React from 'react';
import styles from './MoonshotPopover.less';
import Icon, { IconName } from 'app/common/components/controls/icon';
import {
  Button,
  ButtonSizes,
} from 'app/common/components/controls/button/button';
import {
  useMoonshotPopoverQuery,
  useMoonshotUpdateCampaignMutation,
  Credit,
  useMoonshotPopoverOrgCreditsQuery,
} from '@trello/graphql/generated';
import { OrganizationFeatures } from '@trello/product-features';
import RouterLink from 'app/common/components/singlespa/router-link';
import { ComponentWrapper } from 'app/src/components/ComponentWrapper';
import { idToDate } from '@trello/dates';
import { Noun, Verb, trackUe } from '@trello/analytics';
import { MoonshotTestIds, TestId } from '@trello/webdriverio-tests';
import { Campaigns } from 'app/common/data/campaigns';
import { Analytics } from '@trello/atlassian-analytics';
import { useFeatureFlag } from '@trello/feature-flag-client';
import { forNamespace, localizeCount } from 'app/common/lib/util/i18n';

const format = forNamespace(['moonshot', 'popover']);

interface PopoverListItemProps {
  iconName: IconName;
  content: string;
  href: string;
  testId: TestId;
}

const PopoverListItem: React.FC<PopoverListItemProps> = ({
  iconName,
  content,
  href,
  testId,
}) => {
  const sendGASEvent = useFeatureFlag('dataeng.gasv3-event-tracking', false);

  return (
    <RouterLink
      testId={testId}
      onClick={() => {
        trackUe({
          category: Noun.MOONSHOT,
          verb: Verb.OPENS,
          directObj: Noun.MOONSHOT_WELCOME_POPOVER,
          prepositionalObj: content,
        });

        if (sendGASEvent) {
          Analytics.sendUIEvent({
            action: 'clicked',
            actionSubject: 'link',
            actionSubjectId: 'moonshotExploreLink',
            source: 'moonshotWelcomeInlineDialog',
            attributes: {
              content: content,
            },
          });
        }
      }}
      className={styles.list}
      href={href}
    >
      <Icon className={styles.listIcon} name={iconName} />
      {content}
    </RouterLink>
  );
};

const byDateCreated = (id1: string, id2: string) =>
  idToDate(id1).getTime() - idToDate(id2).getTime();

interface PopoverProps {
  orgId: string;
}

export const MoonshotPopover: React.FC<PopoverProps> = ({ orgId }) => {
  const freeTrialEnabled = useFeatureFlag('btg.bc-free-trial', false);
  const sendGASEvent = useFeatureFlag('dataeng.gasv3-event-tracking', false);

  const { data } = useMoonshotPopoverQuery();
  const [
    updateCampaign,
    { loading: saving },
  ] = useMoonshotUpdateCampaignMutation();

  const org = data?.member?.organizations?.filter((o) => o.id === orgId)[0];

  const myId = data?.member?.id;
  const isMemberAdmin =
    org &&
    org?.memberships?.filter(
      (e) => e.idMember === myId && e.memberType === 'admin',
    ).length > 0;

  const { data: creditsData } = useMoonshotPopoverOrgCreditsQuery({
    variables: {
      orgId,
    },
    skip: !isMemberAdmin,
  });

  if (!org) {
    return null;
  }

  const isOrgFreeTrial =
    freeTrialEnabled &&
    creditsData?.organization?.credits?.filter(
      (v: Credit) => v.type === 'freeTrial',
    ).length! > 0;

  const member = data && data.member;
  if (!member) {
    return null;
  }

  const campaign = member.campaigns.find((c) => c.name === Campaigns.MOONSHOT);
  if (!campaign || campaign.dateDismissed !== null) {
    return null;
  }

  const doesOrgBelongToMoonshot = org.id === orgId;
  if (org && !doesOrgBelongToMoonshot) {
    return null;
  }

  const firstBoard = member.boards
    .filter((board) => board.idOrganization === org.id)
    .sort((a, b) => byDateCreated(a.id, b.id))[0];

  if (!saving) {
    trackUe({
      category: Noun.MOONSHOT,
      verb: Verb.VIEWS,
      directObj: Noun.MOONSHOT_WELCOME_POPOVER,
    });

    if (sendGASEvent) {
      Analytics.sendScreenEvent({
        name: 'moonshotWelcomeInlineDialog',
        containers: {
          organization: {
            id: org?.id || '',
          },
        },
      });
    }
  }

  const numInvited = org.memberships.length - 1;
  const isPremium = OrganizationFeatures.hasProduct(org.products[0]);

  const ExplorePowerUps: PopoverListItemProps = {
    iconName: IconName.PowerUp,
    content: format('powerups link'),
    href: '/power-ups',
    testId: MoonshotTestIds.WelcomePopoverExplorePowerUps,
  };
  const TeamLink: PopoverListItemProps = isPremium
    ? {
        iconName: IconName.Gear,
        content: format('team settings link'),
        href: `/${org.name}/account`,
        testId: MoonshotTestIds.WelcomePopoverExploreTeamSettings,
      }
    : {
        iconName: IconName.BusinessClass,
        content: format('business class link'),
        href: `/${org.name}/billing`,
        testId: MoonshotTestIds.WelcomePopoverExploreBC,
      };

  let ctaLinks = [ExplorePowerUps, TeamLink];

  if (firstBoard) {
    const ExploreBoards: PopoverListItemProps = {
      iconName: IconName.Board,
      content: format('board link'),
      href: `/b/${firstBoard.shortLink}`,
      testId: MoonshotTestIds.WelcomePopoverExploreBoards,
    };

    ctaLinks = [ExploreBoards, ...ctaLinks];
  }

  const onDismiss = async () => {
    if (saving) {
      return;
    }

    trackUe({
      category: Noun.MOONSHOT,
      verb: Verb.DISMISSES,
      directObj: Noun.MOONSHOT_WELCOME_POPOVER,
    });

    if (sendGASEvent) {
      Analytics.sendUIEvent({
        action: 'clicked',
        actionSubject: 'button',
        actionSubjectId: 'dismissButton',
        source: 'moonshotWelcomeInlineDialog',
        containers: {
          organization: {
            id: org?.id || '',
          },
        },
      });
    }

    await updateCampaign({
      variables: {
        id: campaign.id,
        isDismissed: true,
      },
    });
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.popover}>
        <h1>
          {format('popover heading')}{' '}
          <span role="img" aria-label="waving hand">
            👋
          </span>
        </h1>
        {isOrgFreeTrial ? (
          <p className={styles.invite}>
            {format('free trial messaging')}{' '}
            <RouterLink
              testId={MoonshotTestIds.WelcomePopoverFreeTrial}
              onClick={() =>
                trackUe({
                  category: Noun.MOONSHOT,
                  verb: Verb.OPENS,
                  directObj: Noun.MOONSHOT_WELCOME_POPOVER,
                  prepositionalObj: 'billing page',
                })
              }
              href={`/${org.name}/billing`}
            >
              {format('billing link')}
            </RouterLink>
          </p>
        ) : numInvited > 0 ? (
          <p className={styles.invite}>
            {format('invited members messaging', {
              localizedOthersCount: (
                <strong key="localizedOthersCount">
                  {localizeCount('others', numInvited)}
                </strong>
              ),
            })}{' '}
            <RouterLink
              testId={MoonshotTestIds.WelcomePopoverInvite}
              onClick={() => {
                trackUe({
                  category: Noun.MOONSHOT,
                  verb: Verb.OPENS,
                  directObj: Noun.MOONSHOT_WELCOME_POPOVER,
                  prepositionalObj: 'Add Additional Members',
                });

                if (sendGASEvent) {
                  Analytics.sendUIEvent({
                    action: 'clicked',
                    actionSubject: 'link',
                    actionSubjectId: 'inviteTeamMemberLink',
                    source: 'moonshotWelcomeInlineDialog',
                    attributes: {
                      numInvited: numInvited,
                    },
                    containers: {
                      organization: {
                        id: org?.id || '',
                      },
                    },
                  });
                }
              }}
              href={`/${org.name}/members`}
            >
              {format('invite more link')}
            </RouterLink>
          </p>
        ) : (
          <p className={styles.invite}>
            {format('invite members messaging')}{' '}
            <RouterLink
              testId={MoonshotTestIds.WelcomePopoverInvite}
              onClick={() => {
                trackUe({
                  category: Noun.MOONSHOT,
                  verb: Verb.OPENS,
                  directObj: Noun.MOONSHOT_WELCOME_POPOVER,
                  prepositionalObj: 'Add Additional Members',
                });

                if (sendGASEvent) {
                  Analytics.sendUIEvent({
                    action: 'clicked',
                    actionSubject: 'link',
                    actionSubjectId: 'inviteTeamMemberLink',
                    source: 'moonshotWelcomeInlineDialog',
                    attributes: {
                      numInvited: numInvited,
                    },
                    containers: {
                      organization: {
                        id: org?.id || '',
                      },
                    },
                  });
                }
              }}
              href={`/${org.name}/members`}
            >
              {format('invite link')}
            </RouterLink>
          </p>
        )}
        <p className={styles.content}>
          {format('getting started links heading')}{' '}
        </p>
        {ctaLinks.map((props, i) => (
          <PopoverListItem key={i} {...props} />
        ))}
        <Button
          testId={MoonshotTestIds.WelcomePopoverDismiss}
          className={styles.button}
          size={ButtonSizes.FULLWIDTH}
          onClick={onDismiss}
          disabled={saving}
        >
          {format('dismiss button')}
        </Button>
      </div>
      <div className={styles.arrowWrapper}>
        <div className={styles.arrow} />
      </div>
      <div className={styles.iconWrapper}>
        <img
          src={require('resources/images/taco.png')}
          alt=""
          role="presentation"
        />
      </div>
    </div>
  );
};

export const MoonshotPopoverConnected: React.FC<PopoverProps> = (props) => (
  <ComponentWrapper>
    <MoonshotPopover {...props} />
  </ComponentWrapper>
);
